import { HelpCircle, Key, Shield, Smartphone } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Separator } from "../../components/ui/separator";
import { verify2FALogin } from "../auth-client";
import { OTPInput } from "./OTPInput";

interface TwoFactorVerifyProps {
  tempToken: string;
  onComplete: (user: any) => void;
  onError: (error: string) => void;
}

export function TwoFactorVerify({
  tempToken,
  onComplete,
}: TwoFactorVerifyProps) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [otpCode, setOtpCode] = useState("");
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [backupCode, setBackupCode] = useState("");

  const handleVerifyOTP = async () => {
    if (otpCode.length !== 6) {
      setError("Por favor, digite o código de 6 dígitos");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await verify2FALogin(tempToken, otpCode);
      console.log("2FA verification response:", response);
      onComplete(response.user);
    } catch (err: any) {
      setError(err.message || "Código inválido. Verifique e tente novamente.");
      setOtpCode("");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyBackupCode = async () => {
    if (!backupCode.trim()) {
      setError("Por favor, digite um código de backup");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await verify2FALogin(tempToken, backupCode.trim());
      onComplete(response.user);
    } catch (err: any) {
      setError(err.message || "Código de backup inválido.");
      setBackupCode("");
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-6 h-6 text-blue-600" />
          </div>
          <CardTitle className="text-2xl">
            Verificação de Dois Fatores
          </CardTitle>
          <p className="text-muted-foreground">
            Digite o código do seu aplicativo autenticador para continuar
          </p>
        </CardHeader>

        <CardContent className="space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {!useBackupCode ? (
            <>
              {/* TOTP Code Input */}
              <div className="space-y-4">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <Smartphone className="w-5 h-5 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      Código do aplicativo autenticador
                    </span>
                  </div>
                  <OTPInput
                    value={otpCode}
                    onChange={setOtpCode}
                    error={!!error}
                    disabled={loading}
                    onComplete={handleVerifyOTP}
                  />
                </div>

                <Button
                  onClick={handleVerifyOTP}
                  className="w-full"
                  disabled={otpCode.length !== 6 || loading}
                >
                  {loading ? "Verificando..." : "Verificar"}
                </Button>
              </div>

              <Separator />

              {/* Backup Code Option */}
              <div className="text-center">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setUseBackupCode(true)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <Key className="w-4 h-4 mr-2" />
                  Usar código de backup
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Backup Code Input */}
              <div className="space-y-4">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <Key className="w-5 h-5 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      Código de backup
                    </span>
                  </div>

                  <Alert className="mb-4">
                    <HelpCircle className="h-4 w-4" />
                    <AlertDescription>
                      Digite um dos códigos de backup que você salvou durante a
                      configuração inicial do 2FA. Cada código só pode ser usado
                      uma vez.
                    </AlertDescription>
                  </Alert>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="backupCode">Código de backup</Label>
                  <Input
                    id="backupCode"
                    type="text"
                    placeholder="Digite seu código de backup"
                    value={backupCode}
                    onChange={(e) => setBackupCode(e.target.value)}
                    disabled={loading}
                    className={error ? "border-red-500" : ""}
                  />
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setUseBackupCode(false);
                      setBackupCode("");
                      setError("");
                    }}
                    className="flex-1"
                    disabled={loading}
                  >
                    Voltar
                  </Button>
                  <Button
                    onClick={handleVerifyBackupCode}
                    className="flex-1"
                    disabled={!backupCode.trim() || loading}
                  >
                    {loading ? "Verificando..." : "Verificar"}
                  </Button>
                </div>
              </div>
            </>
          )}

          <Separator />

          {/* Help and Back to Login */}
          <div className="text-center space-y-2">
            <p className="text-sm text-muted-foreground">
              Não consegue acessar seu aplicativo autenticador?
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBackToLogin}
              className="text-muted-foreground hover:text-foreground"
            >
              Voltar ao login
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
